// OtpInputComponent.js
import React, { useEffect, useRef, useState } from 'react';
import { Box, InputBase, Typography } from '@mui/material';
import { styled } from '@mui/system';
import LoadingDots from '../LoadingDots/LoadingDots';
import { gsap } from 'gsap';

const OtpBox = styled(InputBase)(({ theme }) => ({
  width: 'clamp(42px, 11vw, 54px)',
  height: 'clamp(42px, 11vw, 53px)',
  textAlign: 'center',
  fontSize: 'clamp(1rem, 3.8vw, 1.5rem)',
  fontFamily: theme.typography.fontFamily,
  fontWeight: 500,
  color: theme.palette.component?.title || '#1a2027',
  border: `1px solid ${theme.palette.component?.border || '#e0e0e0'}`,
  borderRadius: 12,
  backgroundColor: theme.palette.background.default,
  boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
  outline: 'none',
  transition: 'border-color 0.2s ease',
  '&:focus-within': {
    borderColor: theme.palette.component?.valid || theme.palette.secondary.main,
  },
  input: {
    textAlign: 'center',
    padding: 0,
  },
}));

const OtpInputComponent = ({
  name,
  length = 4,
  onChange,
  autoSubmit,
  loading = false,
  resetCounter = 0,
  errorText = '',
  isMobile = false,
  label = 'Enter your code:',
}) => {
  const L = Math.max(1, Number(length) || 4);
  const [otp, setOtp] = useState(Array(L).fill(''));
  const inputsRef = useRef([]);
  const containerRef = useRef(null);

  // Clear all boxes whenever resetCounter changes
  useEffect(() => {
    if (errorText) {
      setOtp(Array(L).fill(''));
      // focus first box after clear
      setTimeout(() => {
        const el = inputsRef.current[0];
        if (el && el.focus) el.focus();
      }, 0);
    }
    // If there’s an error, play shake + red flash
    if (errorText && containerRef.current) {
      const nodes = containerRef.current.querySelectorAll('.otp-box');

      nodes.forEach((node) => {
        // capture the current/computed border color to restore later
        const original = window.getComputedStyle(node).borderColor;

        const tl = gsap.timeline();
        tl.set(node, { borderColor: '#d32f2f' })
          .to(node, {
            x: -5,
            duration: 0.08,
            yoyo: true,
            repeat: 5,
            ease: 'power1.inOut',
          })
          .to(node, { x: 0, duration: 0.08 }) // settle back
          // fade back to original border color
          .to(
            node,
            {
              borderColor: original,
              duration: 0.1,
              onComplete: () => {
                // optional: hand control back to CSS rules (focus/hover/etc.)
                gsap.set(node, { clearProps: 'borderColor' });
              },
            },
            '<'
          ); // overlap the settle animation a bit
      });
    }
  }, [resetCounter, L, errorText]);

  const focusIndex = (i) => {
    const el = inputsRef.current[i];
    if (el && typeof el.focus === 'function') el.focus();
  };

  const emitChange = (nextArr) => {
    const joined = nextArr.join('');
    onChange?.(name, joined);
    return joined;
  };

  const tryAutoSubmit = (arr) => {
    // Only after all filled
    if (arr.some((c) => c === '')) return;
    const full = arr.join('');
    // Defer to next task so parent state (from onChange) is flushed
    setTimeout(() => {
      autoSubmit?.(full);
    }, 0);
  };

  const handleKeyDown = (e, index) => {
    if (loading) {
      e.preventDefault();
      return;
    }
    const key = e.key;

    if (key === 'Backspace') {
      e.preventDefault();
      setOtp((prev) => {
        const next = [...prev];
        if (next[index]) {
          next[index] = '';
          emitChange(next);
          return next;
        }
        // if empty, move left and clear there
        const prevIdx = Math.max(0, index - 1);
        next[prevIdx] = '';
        emitChange(next);
        setTimeout(() => focusIndex(prevIdx), 0);
        return next;
      });
      return;
    }

    if (key === 'ArrowLeft') {
      e.preventDefault();
      focusIndex(Math.max(0, index - 1));
      return;
    }
    if (key === 'ArrowRight') {
      e.preventDefault();
      focusIndex(Math.min(L - 1, index + 1));
      return;
    }

    // Only accept digit keys (let onChange handler handle the char)
    if (/^\d$/.test(key)) {
      // we handle this in onChange; prevent double input
      // (optional: you could set the char here, but onChange is enough)
    }
  };

  const handlePaste = (e, index) => {
    if (loading) return;
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (pasted.length <= 1) return; // let onChange handle single chars
    e.preventDefault();
    handleChange(index, pasted);
  };

  const handleChange = (index, raw) => {
    if (loading) return;
    // digits only
    const clean = String(raw).replace(/\D/g, '');
    if (clean.length === 0) return;

    setOtp((prev) => {
      const next = [...prev];
      // Support paste of multiple digits across boxes
      let cursor = index;
      for (const ch of clean) {
        if (cursor >= L) break;
        next[cursor] = ch; // keep as string (preserves leading zeros)
        cursor++;
      }

      emitChange(next);

      // Move focus
      if (cursor < L) {
        setTimeout(() => focusIndex(cursor), 0);
      } else {
        setTimeout(() => focusIndex(L - 1), 0);
      }

      tryAutoSubmit(next);
      return next;
    });
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" my={0}>
      {label && (
        <Typography
          className="stagger-in"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '1.5rem',
            lineHeight: '1.75rem',
            letterSpacing: '-0.03em',
            color: '#162F3B',
            textAlign: 'center',
            mb: '25px',
            whiteSpace: 'pre-line',
          }}
        >
          {label}
        </Typography>
      )}
      <Box
        ref={containerRef}
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap={isMobile ? 1 : 2}
        flexWrap="nowrap"
        sx={{
          width: '100%',
        }}
      >
        {otp.map((digit, index) => (
          <OtpBox
            key={index}
            inputRef={(el) => (inputsRef.current[index] = el)}
            type="text"
            disabled={loading}
            inputMode="numeric"
            value={digit}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={(e) => handlePaste(e, index)}
            onChange={(e) => handleChange(index, e.target.value)}
            className="otp-box"
            inputProps={{
              pattern: '[0-9]*',
              autoComplete: 'one-time-code',
              'aria-label': `OTP digit ${index + 1}`,
              style: { textAlign: 'center' },
            }}
          />
        ))}
      </Box>

      {/* {!!errorText && (
        <Typography sx={{ mt: 1, fontSize: '0.875rem', color: '#d32f2f' }}>
          {errorText}
        </Typography>
      )} */}

      {loading && (
        <Box mt={2}>
          <LoadingDots variant="otp" />
        </Box>
      )}
    </Box>
  );
};

export default OtpInputComponent;
